import { useState, useCallback, useReducer, useEffect } from "react";
import useSWR from "swr";
import * as Sentry from "@sentry/react";
import { produce } from "immer";
import { v4 as uuidv4 } from "uuid";

import { EAIClient } from "Services/EAIClient";
import { Utils } from "../common/utils";
import { DomainCompetency } from "Stores/courseStore";
import { useAppContext } from "@/contexts/app";
import { CompetencyItem } from "src/reducers/domainCompetencyReducer";
import { useStandardCompetencies } from "./standard-competency";
import { useToast } from "./useToast";

const clientConfigs = Utils.getEAIClientConfigs();
const client = new EAIClient();

export interface DomainCompetencyResponse {
    data: DomainCompetency[];
    code: number;
    message: string;
}

export type DomainCompetencyAction =
    | { type: "SET_ITEMS"; payload: DomainCompetency[] }
    | {
          type: "ADD_ITEM";
          payload: { parentRuuid: string | null; level: number };
      }
    | { type: "DELETE_ITEM"; payload: { ruuid: string } }
    | { type: "UPDATE_ITEM"; payload: { ruuid: string; name: string } }
    | { type: "TOGGLE_ENABLED"; payload: { ruuid: string; enabled: boolean } }
    | { type: "TOGGLE_OPEN"; payload: { ruuid: string } };

interface DomainCompetencyState {
    items: CompetencyItem[];
    isLoading: boolean;
    error: Error | null;
}

const initialState: DomainCompetencyState = {
    items: [],
    isLoading: false,
    error: null,
};

const isTemporaryItem = (ruuid: string): boolean => {
    return ruuid.startsWith("temp-");
};

const findItemInTree = (
    items: CompetencyItem[],
    ruuid: string
): CompetencyItem | undefined => {
    for (const item of items) {
        if (item.ruuid === ruuid) return item;

        const found = findItemInTree(item.children, ruuid);
        if (found) return found;
    }
    return undefined;
};

const domainCompetencyReducer = produce(
    (draft: DomainCompetencyState, action: DomainCompetencyAction) => {
        switch (action.type) {
            case "SET_ITEMS": {
                draft.items = action.payload as CompetencyItem[];
                return;
            }

            case "ADD_ITEM": {
                const { parentRuuid, level } = action.payload;
                const newRuuid = "temp-" + uuidv4();

                const newItem: CompetencyItem = {
                    ruuid: newRuuid,
                    schoold_id: clientConfigs.school || "",
                    standard_name: "",
                    standard_item: "",
                    enflux_created: "",
                    enflux_updated: "",
                    standard_level: level,
                    enabled: true,
                    children: [],
                    isOpen: false,
                };

                if (parentRuuid) {
                    newItem.domain_competency_parent_ruuid = parentRuuid;

                    const addToParent = (items: CompetencyItem[]) => {
                        for (let i = 0; i < items.length; i++) {
                            if (items[i].ruuid === parentRuuid) {
                                items[i].isOpen = true;
                                items[i].children.push(newItem);
                                return true;
                            }
                            if (items[i].children.length > 0) {
                                if (addToParent(items[i].children)) return true;
                            }
                        }
                        return false;
                    };

                    addToParent(draft.items);
                } else {
                    draft.items.push(newItem);
                }
                return;
            }

            case "DELETE_ITEM": {
                const { ruuid } = action.payload;

                const removeItem = (items: CompetencyItem[]): boolean => {
                    const index = items.findIndex(
                        (item) => item.ruuid === ruuid
                    );

                    if (index !== -1) {
                        items.splice(index, 1);
                        return true;
                    }

                    for (let i = 0; i < items.length; i++) {
                        if (removeItem(items[i].children)) return true;
                    }

                    return false;
                };

                removeItem(draft.items);
                return;
            }

            case "UPDATE_ITEM": {
                const { ruuid, name } = action.payload;

                const updateItem = (items: CompetencyItem[]): boolean => {
                    for (let i = 0; i < items.length; i++) {
                        if (items[i].ruuid === ruuid) {
                            items[i].standard_name = name;
                            items[i].standard_item = name;
                            return true;
                        }
                        if (items[i].children.length > 0) {
                            if (updateItem(items[i].children)) return true;
                        }
                    }
                    return false;
                };

                updateItem(draft.items);
                return;
            }

            case "TOGGLE_ENABLED": {
                const { ruuid, enabled } = action.payload;

                const toggleItem = (items: CompetencyItem[]): boolean => {
                    for (let i = 0; i < items.length; i++) {
                        if (items[i].ruuid === ruuid) {
                            items[i].enabled = enabled;
                            items[i].school_enabled = enabled;
                            return true;
                        }
                        if (items[i].children.length > 0) {
                            if (toggleItem(items[i].children)) return true;
                        }
                    }
                    return false;
                };

                toggleItem(draft.items);
                return;
            }

            case "TOGGLE_OPEN": {
                const { ruuid } = action.payload;

                const toggleOpen = (items: CompetencyItem[]): boolean => {
                    for (let i = 0; i < items.length; i++) {
                        if (items[i].ruuid === ruuid) {
                            items[i].isOpen = !items[i].isOpen;
                            return true;
                        }
                        if (items[i].children.length > 0) {
                            if (toggleOpen(items[i].children)) return true;
                        }
                    }
                    return false;
                };

                toggleOpen(draft.items);
                return;
            }
        }
    }
);

export function useDomainCompetencies() {
    const [openUploadModal, setOpenUploadModal] = useState(false);
    const [
        openDomainStandardCompetenciesModal,
        setOpenDomainStandardCompetenciesModal,
    ] = useState(false);
    // const [showToast, setShowToast] = useState(false);
    // const [toastMessage, setToastMessage] = useState("");
    // const [toastType, setToastType] = useState<"success" | "error">("success");
    const [isRequestLoading, setIsRequestLoading] = useState(false);
    const { dispatchError } = useAppContext();
    const [state, dispatch] = useReducer(domainCompetencyReducer, initialState);
    const { mutate: mutateStandardCompetencies } = useStandardCompetencies();

    const { toast } = useToast()

    const { data, error, isLoading, mutate } = useSWR<DomainCompetencyResponse>(
        `${clientConfigs.endpoint}/schools/${clientConfigs.school}/curriculum_mapping/domain_competency?enabled_only=false`
    );

    useEffect(() => {
        if (data?.data) {
            dispatch({ type: "SET_ITEMS", payload: data.data });
        }
    }, [data]);

    const addItem = useCallback(
        async (parentRuuid: string | null, level = 1) => {
            dispatch({
                type: "ADD_ITEM",
                payload: { parentRuuid, level },
            });

            return findItemInTree(state.items, "temp-");
        },
        [state.items]
    );

    const updateNameAndSave = useCallback(
        async (ruuid: string, name: string) => {
            dispatch({
                type: "UPDATE_ITEM",
                payload: { ruuid, name },
            });

            try {
                setIsRequestLoading(true);
                const item = findItemInTree(state.items, ruuid);
                if (!item) return;

                if (isTemporaryItem(ruuid)) {
                    const { ruuid: _, ...newDomainCompetencyWithoutRuuid } = {
                        ...item,
                        standard_name: name,
                        standard_item: name,
                    };
                    await client.createDomainCompetency(
                        newDomainCompetencyWithoutRuuid
                    );
                    toast({
                        type: "success",
                        title: "Domain Competency Created",
                        message: "Domain competency created successfully",
                    })
                } else {
                    const updatedItem = {
                        ...item,
                        standard_name: name,
                        standard_item: name,
                    };
                    await client.updateDomainCompetency(updatedItem, ruuid);
                    toast({
                        type: "success",
                        title: "Domain Competency Updated",
                        message: "Domain competency updated successfully",
                    })
                }
            } catch (error) {
                Sentry.captureException(error);
                dispatchError(error);
                toast({
                    type: "error",
                    title: "Update failed",
                    message: "Failed to update domain competency",
                })
                await mutate();
            } finally {
                setIsRequestLoading(false);
            }
        },
        [state.items, dispatchError, mutate]
    );

    const deleteItem = useCallback(
        async (ruuid: string) => {
            dispatch({
                type: "DELETE_ITEM",
                payload: { ruuid },
            });

            try {
                if (!isTemporaryItem(ruuid)) {
                    setIsRequestLoading(true);
                    await client.deleteDomainCompetency(ruuid);
                    toast({
                        type: "success",
                        title: "Domain Competency Deleted",
                        message: "Domain competency deleted successfully",
                    })
                    await mutate();
                    await mutateStandardCompetencies();
                }
            } catch (error) {
                Sentry.captureException(error);
                dispatchError(error);
                toast({
                    type: "error",
                    title: "Deletion failed",
                    message: "Failed to delete domain competency",
                })
                await mutate();
            } finally {
                setIsRequestLoading(false);
            }
        },
        [dispatchError, mutate, mutateStandardCompetencies]
    );

    const toggleEnabled = useCallback(
        async (ruuid: string) => {
            const item = findItemInTree(state.items, ruuid);
            if (!item) return;

            const isCurrentlyEnabled =
                item.school_enabled !== undefined
                    ? item.school_enabled
                    : item.enabled;

            dispatch({
                type: "TOGGLE_ENABLED",
                payload: { ruuid, enabled: !isCurrentlyEnabled },
            });

            try {
                if (!isTemporaryItem(ruuid)) {
                    setIsRequestLoading(true);
                    await client.toggleDomainCompetency(
                        !isCurrentlyEnabled,
                        ruuid
                    );
                    toast({
                        type: "success",
                        title: "Domain Competency Updated",
                        message: `Domain competency ${!isCurrentlyEnabled ? "enabled" : "disabled"} successfully`,
                    })
                }
            } catch (error) {
                Sentry.captureException(error);
                dispatchError(error);
                toast({
                    type: "error",
                    title: "Toggle failed",
                    message: "Failed to toggle domain competency",
                })
                await mutate();
            } finally {
                setIsRequestLoading(false);
            }
        },
        [state.items, dispatchError, mutate]
    );

    interface UploadDomainCompetencyFileProps {
        file: File;
    }

    const uploadDomainCompetencyFile = useCallback(
        async ({
            file,
        }: UploadDomainCompetencyFileProps): Promise<string | void> => {
            try {
                setIsRequestLoading(true);
                const formData = new FormData();
                formData.append("file", file);

                await client.uploadDomainCompetency(formData);
                await mutate();
                toast({
                    type: "success",
                    title: "File uploaded",
                    message: "File uploaded successfully",
                })
                return "File uploaded successfully.";
            } catch (error) {
                Sentry.captureException(error);
                dispatchError(error);
                toast({
                    type: "error",
                    title: "Upload failed",
                    message: "Failed to upload file",
                })
                throw error;
            } finally {
                setIsRequestLoading(false);
            }
        },
        [mutate, dispatchError]
    );

    const toggleOpen = useCallback((ruuid: string) => {
        dispatch({
            type: "TOGGLE_OPEN",
            payload: { ruuid },
        });
    }, []);

    return {
        data: {
            data: state.items,
            code: data?.code || 0,
            message: data?.message || "",
        },
        items: state.items,
        openUploadModal,
        setOpenUploadModal,
        addItem,
        deleteItem,
        toggleEnabled,
        updateNameAndSave,
        uploadDomainCompetencyFile,
        isLoading,
        isRequestLoading,
        error,
        openDomainStandardCompetenciesModal,
        setOpenDomainStandardCompetenciesModal,
        mutate,
        toggleOpen
    };
}
